import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession, useCreateSession, useSendMessage } from '@/hooks/useApi';
import { useAppStore } from '@/stores/appStore';
import { getErrorMessage } from '@/lib/errors';
import type { Session, Content } from '@/types';

interface SessionContextType {
  currentSession: Session | null;
  isLoading: boolean;
  error: string | null;
  createNewSession: () => Promise<void>;
  createSessionWithMessage: (message: string) => Promise<Session>;
  switchToSession: (sessionId: string) => void;
  isInWelcomeState: boolean;
  retrySessionCreation: (message: string) => Promise<Session>;
  clearError: () => void;
  sessionCreationError: string | null;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export function useSessionContext() {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionProvider');
  }
  return context;
}

interface SessionProviderProps {
  children: React.ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps) {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAppStore();
  const [currentSession, setCurrentSession] = useState<Session | null>(null);
  const [sessionCreationError, setSessionCreationError] = useState<string | null>(null);
  const creatingSessionRef = useRef(false);
  const lastFailedMessageRef = useRef<string | null>(null);

  const {
    data: session,
    isLoading: sessionLoading,
    error: sessionError
  } = useSession(sessionId);

  const createSessionMutation = useCreateSession();
  const sendMessageMutation = useSendMessage();

  // Update current session when session data changes
  useEffect(() => {
    if (session && sessionId === session.id) {
      setCurrentSession(session);
    } else if (!sessionId) {
      setCurrentSession(null);
    }
  }, [session, sessionId]);

  // Determine if we're in welcome state (no sessionId in URL and user is authenticated)
  const isInWelcomeState = user.authenticated && !sessionId;

  const createNewSession = useCallback(async () => {
    if (!user.authenticated || creatingSessionRef.current) return;

    try {
      creatingSessionRef.current = true;
      const newSession = await createSessionMutation.mutateAsync(undefined);
      navigate(`/chat/${newSession.id}`, { replace: true });
    } catch (error) {
      console.error('Failed to create session:', error);
    } finally {
      creatingSessionRef.current = false;
    }
  }, [user.authenticated, createSessionMutation.mutateAsync, navigate]);

  const createSessionWithMessage = useCallback(async (message: string): Promise<Session> => {
    if (!user.authenticated || creatingSessionRef.current) {
      throw new Error('Cannot create session: user not authenticated or session creation in progress');
    }

    try {
      creatingSessionRef.current = true;
      setSessionCreationError(null);
      lastFailedMessageRef.current = message;
      
      // Create the message content
      const content: Content = {
        role: 'user',
        parts: [{ text: message }]
      };
      
      // Use sendMessage without sessionId - backend will create new session
      const response = await sendMessageMutation.mutateAsync({ 
        content,
        sessionId: undefined // No sessionId means create new session
      });
      
      // Clear any previous error and stored message
      setSessionCreationError(null);
      lastFailedMessageRef.current = null;
      
      // Navigate to the new session
      navigate(`/chat/${response.sessionId}`, { replace: true });
      
      // Fetch the full session data
      // The session will be loaded by the useSession hook when we navigate
      return {
        id: response.sessionId,
        appName: '',
        userId: user.id,
        state: {},
        events: response.events,
        lastUpdateTime: new Date().toISOString(),
      } as Session;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('Failed to create session with message:', error);
      
      // Store the error for display in welcome state
      setSessionCreationError(errorMessage);
      
      // Keep the message for retry
      lastFailedMessageRef.current = message;
      
      throw error;
    } finally {
      creatingSessionRef.current = false;
    }
  }, [user.authenticated, user.id, sendMessageMutation.mutateAsync, navigate]);

  const switchToSession = (newSessionId: string) => {
    navigate(`/chat/${newSessionId}`);
  };

  const retrySessionCreation = useCallback(async (message: string): Promise<Session> => {
    // Use the provided message or the last failed message
    const messageToRetry = message || lastFailedMessageRef.current;
    if (!messageToRetry) {
      throw new Error('No message available for retry');
    }
    
    return createSessionWithMessage(messageToRetry);
  }, [createSessionWithMessage]);

  const clearError = useCallback(() => {
    setSessionCreationError(null);
    lastFailedMessageRef.current = null;
  }, []);

  const isLoading = sessionLoading || createSessionMutation.isPending || sendMessageMutation.isPending;
  const error = sessionError ?
    (sessionError instanceof Error ? sessionError.message : 'Failed to load session') :
    createSessionMutation.error ?
      (createSessionMutation.error instanceof Error ? createSessionMutation.error.message : 'Failed to create session') :
      sendMessageMutation.error ?
        (sendMessageMutation.error instanceof Error ? sendMessageMutation.error.message : 'Failed to send message') :
        null;

  const contextValue: SessionContextType = {
    currentSession,
    isLoading,
    error,
    createNewSession,
    createSessionWithMessage,
    switchToSession,
    isInWelcomeState,
    retrySessionCreation,
    clearError,
    sessionCreationError,
  };

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  );
}